import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/services/api';

const AppContext = createContext();

export function AppProvider({ children }) {
  // Current user - null by default (not logged in)
  const [currentUser, setCurrentUser] = useState(null);

  // Reference data
  const [secteurs, setSecteurs] = useState([]);
  const [categoriesCACES, setCategoriesCACES] = useState([]);
  const [typesVisites, setTypesVisites] = useState([]);
  const [typesFormations, setTypesFormations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [managers, setManagers] = useState([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Logout function - defined early so it can be used in effects
  const logout = useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    api.clearTokens();
    // Clear any cached data
    setRoles([]);
    setCollaborateurs([]);
    setManagers([]);
    setInitialized(false);
  }, []);

  // Listen for logout events (from API when token expires)
  useEffect(() => {
    const handleLogout = () => {
      logout();
      // Don't redirect if already on a public page
      const publicPaths = ['/login', '/reset-password', '/forgot-password', '/invitation', '/verify', '/politique-confidentialite'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));
      
      if (!isPublicPage) {
        window.location.href = '/login';
      }
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [logout]);

  // Check for saved user and validate token on mount
  useEffect(() => {
    const validateSession = async () => {
      // Check for stored tokens
      const savedUser = localStorage.getItem('user');
      const accessToken = api.getAccessToken();
      
      if (savedUser && accessToken) {
        try {
          // Validate token by fetching current user (includes fresh photo_data)
          const user = await api.getCurrentUser();
          setCurrentUser(user);
          
          // Store user without large photo_data
          const userForStorage = { ...user };
          if (user.photo_data) {
            userForStorage.has_photo = true;
            delete userForStorage.photo_data;
          }
          localStorage.setItem('user', JSON.stringify(userForStorage));
          
          // If we're on login page but have a valid session, redirect to dashboard
          const currentPath = window.location.pathname;
          if (currentPath === '/login') {
            window.location.href = '/';
          }
        } catch (e) {
          // Token invalid, clear everything
          console.log('Session expired, clearing...');
          logout();
        }
      } else if (savedUser) {
        // No token but has saved user - clear
        logout();
      }
      
      setLoading(false);
    };
    
    validateSession();
  }, [logout]);

  // Initialize app data when user is logged in
  useEffect(() => {
    const initializeApp = async () => {
      // Don't initialize on public pages
      const publicPaths = ['/login', '/reset-password', '/forgot-password', '/invitation', '/verify', '/politique-confidentialite'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));
      
      if (isPublicPage) {
        setInitialized(true);
        return;
      }
      
      // Check if user is logged in
      if (!currentUser) {
        return;
      }
      
      // Don't reload if already initialized with data
      if (initialized && secteurs.length > 0) {
        return;
      }
      
      try {
        setLoading(true);
        
        // Initialize default data (only for Admin role)
        if (currentUser.role === 'Admin') {
          try {
            await api.initData();
          } catch (e) {
            // Ignore init-data errors (may already be initialized)
            console.log('Init data skipped or already done');
          }
        }
        
        // Load reference data
        const [secteursData, categoriesData, visitesData, formationsData, rolesData, collaborateursData] = await Promise.all([
          api.getSecteurs(),
          api.getCategoriesCACES(),
          api.getTypesVisites(),
          api.getTypesFormations(),
          api.getRoles(),
          api.getCollaborateurs()
        ]);

        setSecteurs(secteursData);
        setCategoriesCACES(categoriesData);
        setTypesVisites(visitesData);
        setTypesFormations(formationsData);
        setRoles(rolesData);
        setCollaborateurs(collaborateursData);

        // Load managers - only for roles that can access utilisateurs API
        if (['Admin', 'HSE', 'RH', 'Directeur', 'Manager'].includes(currentUser.role)) {
          try {
            const users = await api.getUtilisateurs();
            const managersData = users.filter(u => u.actif !== false && u.role !== 'Collaborateur');
            setManagers(managersData);
          } catch (e) {
            console.log('Could not load managers list');
          }
        }

        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [currentUser, initialized, secteurs.length]);

  // Login function - also triggers data reload
  const login = (user, tokens = null) => {
    setCurrentUser(user);
    
    // Don't store photo_data in localStorage (too large for 5MB limit)
    // Just store a flag indicating photo exists
    const userForStorage = { ...user };
    if (user.photo_data) {
      userForStorage.has_photo = true;
      delete userForStorage.photo_data;  // Remove large base64 from localStorage
    }
    localStorage.setItem('user', JSON.stringify(userForStorage));
    
    // Store tokens if provided (new JWT flow)
    if (tokens) {
      api.setTokens(tokens.access_token, tokens.refresh_token);
    }
    
    // Reset initialized to force data reload
    setInitialized(false);
  };

  // Logout is defined above with useCallback

  // Refresh functions
  const refreshSecteurs = async () => {
    const data = await api.getSecteurs();
    setSecteurs(data);
  };

  const refreshCategoriesCACES = async () => {
    const data = await api.getCategoriesCACES();
    setCategoriesCACES(data);
  };

  const refreshTypesVisites = async () => {
    const data = await api.getTypesVisites();
    setTypesVisites(data);
  };

  const refreshTypesFormations = async () => {
    const data = await api.getTypesFormations();
    setTypesFormations(data);
  };

  const refreshRoles = async () => {
    const data = await api.getRoles();
    setRoles(data);
  };

  const refreshCollaborateurs = async () => {
    const data = await api.getCollaborateurs();
    setCollaborateurs(data);
  };

  const refreshManagers = async () => {
    // Only roles with access can refresh managers
    if (currentUser && ['Admin', 'HSE', 'RH', 'Directeur', 'Manager'].includes(currentUser.role)) {
      try {
        const users = await api.getUtilisateurs();
        // All active users except those with "Collaborateur" role can be managers
        const managersData = users.filter(u => u.actif !== false && u.role !== 'Collaborateur');
        setManagers(managersData);
      } catch (e) {
        console.log('Could not refresh managers');
      }
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    secteurs,
    categoriesCACES,
    typesVisites,
    typesFormations,
    roles,
    collaborateurs,
    managers,
    loading,
    initialized,
    refreshSecteurs,
    refreshCategoriesCACES,
    refreshTypesVisites,
    refreshTypesFormations,
    refreshRoles,
    refreshCollaborateurs,
    refreshManagers
  };

  // Role-based access helper functions
  const hasRole = (allowedRoles) => {
    if (!currentUser?.role) return false;
    return allowedRoles.includes(currentUser.role);
  };

  const isAdmin = () => hasRole(['Admin']);
  const isHSE = () => hasRole(['Admin', 'HSE']);
  const isDirecteur = () => hasRole(['Admin', 'Directeur']);
  const isRH = () => hasRole(['Admin', 'RH']);
  const isManager = () => hasRole(['Admin', 'Manager']);
  const canManageVisitesMedicales = () => hasRole(['Admin', 'RH', 'HSE']);
  const canManageCACS = () => hasRole(['Admin', 'HSE']);
  const canManageAutorisations = () => hasRole(['Admin', 'HSE', 'Directeur']);
  const canValidateHSE = () => hasRole(['Admin', 'HSE']);
  const canSignAsDirecteur = () => hasRole(['Admin', 'Directeur']);
  const canViewDocuments = (entityType = null) => {
    // Manager can view CACES, Formation and demande_hse documents, but NOT Visite Médicale
    if (currentUser?.role === 'Manager') {
      if (entityType === 'visite_medicale' || entityType === 'visite') {
        return false;
      }
      return true; // Can view CACES, Formation and demande_hse documents
    }
    return hasRole(['Admin', 'RH', 'HSE', 'Directeur']);
  };
  const canManageSettings = () => hasRole(['Admin', 'HSE', 'Directeur', 'RH', 'Manager', 'Maintenance']);

  const extendedValue = {
    ...value,
    hasRole,
    isAdmin,
    isHSE,
    isDirecteur,
    isRH,
    isManager,
    canManageVisitesMedicales,
    canManageCACS,
    canManageAutorisations,
    canValidateHSE,
    canSignAsDirecteur,
    canViewDocuments,
    canManageSettings
  };

  return (
    <AppContext.Provider value={extendedValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}

export default AppContext;
